/**
 * @file routes/(public)/apply.$token.tsx
 * @description 申請表客戶線上填寫公開頁（序110，token 制免登入，參考 Google 表單體驗）。
 *
 * 客戶自管理組交付的連結進入，填寫聯絡資訊/品項/驗證範圍/面積/備註後提交；
 * 提交內容寫入案件 online_application，管理組在案件詳情可見「已填寫」並一鍵套用。
 * 可重複提交，後提交覆蓋前次。
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Result, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/(public)/apply/$token')({ component: PublicApplyPage });

/** 填寫上下文（GET /api/public/application/:token） */
interface ApplyContext {
  caseCode: string;
  scheme: string;
  clientName: string;
  onlineApplication: { submittedAt: string; data: Record<string, unknown> } | null;
}

interface FormValues {
  contactName?: string;
  contactPhone?: string;
  productItems?: string;
  productScopes?: string;
  verificationArea?: string;
  notes?: string;
}

function PublicApplyPage() {
  const { token } = Route.useParams();
  const [form] = Form.useForm<FormValues>();
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, isError } = useQuery<{ item: ApplyContext }>({
    queryKey: ['public-apply', token],
    queryFn: () => apiClient.get(`/api/public/application/${token}`),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (v: FormValues) =>
      apiClient.post(`/api/public/application/${token}`, {
        contactName: v.contactName,
        contactPhone: v.contactPhone,
        // 多筆品項/範圍以頓號或換行分隔輸入，拆成陣列提交
        productItems: v.productItems
          ?.split(/[、,，\n]/)
          .map((x) => x.trim())
          .filter(Boolean),
        productScopes: v.productScopes
          ?.split(/[、,，\n]/)
          .map((x) => x.trim())
          .filter(Boolean),
        verificationArea: v.verificationArea ? String(v.verificationArea) : undefined,
        notes: v.notes,
      }),
    onSuccess: () => setSubmitted(true),
    onError: (err: Error) => {
      console.error('[PublicApplyPage] submit failed', err);
    },
  });

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f7fa',
        display: 'flex',
        justifyContent: 'center',
        padding: '32px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 640 }}>{children}</div>
    </div>
  );

  if (isLoading) return shell(<Card loading />);
  if (isError || !data?.item) {
    return shell(
      <Result
        status="404"
        title="連結無效或已失效"
        subTitle="請聯繫中華驗證管理組重新取得填寫連結"
      />,
    );
  }

  if (submitted) {
    return shell(
      <Result
        status="success"
        title="申請表已提交"
        subTitle="中華驗證管理組將核對您的填寫內容；如需修改可重新開啟本連結再次提交（覆蓋前次）。"
        extra={<Button onClick={() => setSubmitted(false)}>重新填寫</Button>}
      />,
    );
  }

  const ctx = data.item;
  return shell(
    <Card
      title={
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            驗證申請表線上填寫
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {ctx.clientName}・案件 {ctx.caseCode}・方案 {ctx.scheme}
          </Typography.Text>
        </div>
      }
    >
      {ctx.onlineApplication && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`您已於 ${new Date(ctx.onlineApplication.submittedAt).toLocaleString('zh-TW')} 提交過，再次提交將覆蓋前次內容。`}
        />
      )}
      {submit.isError && (
        <Alert style={{ marginBottom: 16 }} type="error" showIcon message="提交失敗，請稍後再試" />
      )}
      <Form<FormValues> form={form} layout="vertical" onFinish={(v) => submit.mutate(v)}>
        <Form.Item name="contactName" label="聯絡人姓名">
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="contactPhone" label="聯絡電話">
          <Input maxLength={50} />
        </Form.Item>
        <Form.Item name="productItems" label="產品品項" tooltip="多筆以頓號（、）或換行分隔">
          <Input.TextArea rows={2} maxLength={1000} placeholder="例：苦茶、水稻" />
        </Form.Item>
        <Form.Item name="productScopes" label="驗證範圍" tooltip="多筆以頓號（、）或換行分隔">
          <Input.TextArea
            rows={2}
            maxLength={1000}
            placeholder="例：苦茶(苦茶菓)、自產農產加工品[苦茶籽]"
          />
        </Form.Item>
        <Form.Item name="verificationArea" label="申請驗證面積（公頃）">
          <InputNumber
            stringMode
            min={0}
            precision={4}
            style={{ width: 240 }}
            placeholder="0.0000"
          />
        </Form.Item>
        <Form.Item name="notes" label="其他說明">
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submit.isPending} block size="large">
          提交申請表
        </Button>
      </Form>
    </Card>,
  );
}
